import { Suspense, lazy, useMemo } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import MainLayout from "../../components/layouts/MainLayout"
import CardLayout from "../../components/layouts/CardLayout"
import { Text, useMainContext, useWindowSize } from "../../modules/Dashboard"
import GlobalLoading from "../../components/layouts/loading/GlobalLoading"
import HistoryIcon from "../../components/icons/HistoryIcon"
import PulseIcon from "../../components/icons/PulseIcon"
import RecycleIcon from "../../components/icons/RecycleIcon"
import ArrowSwapIcon from "../../components/icons/ArrowSwapIcon"
import DollarIcon from "../../components/icons/DollarIcon"
import { TABLET_SIZE } from "../../core/constants/common"

import LeftIcon from "../../components/icons/Left"
import { LayoutGroup, motion } from "framer-motion";

const UserTraceHistory = lazy(() => import("./user-trace"))
const PredictHistoryPage = lazy(() => import("../analyze/components/History"))
const SwapHistory = lazy(() => import("./swap-hist"))
const DepositTransactionsHistory = lazy(() => import("./deposit-hist"))

const BASE_ROUTE = "/transactions"

const SUB_NAVS = [
    {
        title: "analysis-history",
        desc: "predict-history-desc",
        Icon: PulseIcon,
        href: "/transactions/predict",
        image: 'predict-hist',
    },
    // {
    //     title: "swap-history",
    //     desc: "swap-transactions-history-desc",
    //     Icon: RecycleIcon,
    //     href: "/transactions/swap",
    //     image: 'swap-hist',
    // },
    // {
    //     title: "wallet-transactions",
    //     desc: "deposit-transactions-history-desc",
    //     Icon: ArrowSwapIcon,
    //     href: "/transactions/deposit",
    //     image: 'wallet-hist',
    // },
    // {
    //     title: "trace-table",
    //     desc: "trace-transactions-history-desc",
    //     Icon: DollarIcon,
    //     href: "/transactions/trace",
    //     image: 'log-hist',
    // },
]

const SUB_PAGES = [
    PredictHistoryPage,
    SwapHistory,
    DepositTransactionsHistory,
    UserTraceHistory,
]

const DEFAULT_INDEX = 0

const TransactionsPage = () => {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { width } = useWindowSize()

    const isMobile = width < TABLET_SIZE
    const isRootPage = pathname === BASE_ROUTE

    const activeLinkIndex = useMemo(() => {
        const index = SUB_NAVS.findIndex(nav => nav.href === pathname)
        return index !== -1 ? index : DEFAULT_INDEX
    }, [pathname])

    const ActiveSubpage = SUB_PAGES[activeLinkIndex]

    const handleBack = () => {
        navigate(BASE_ROUTE)
    }

    const showMobileBack = isMobile && !isRootPage

    return (
        <MainLayout>
            <div className="space-y-6 pb-6">
                {/* HEADER */}
                <div className="flex items-center justify-between relative ">
                    {/* MOBILE BACK BUTTON */}
                    {showMobileBack ? (
                        <>
                            <button
                                onClick={handleBack}
                                className="flex items-center justify-center w-12 h-10 border border-[#364153] rounded-lg"
                            >
                                <LeftIcon />
                            </button>
                            <Text tid={SUB_NAVS[activeLinkIndex].title} className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 text-xl" />
                        </>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="flex items-center justify-center w-10 h-10 bg-[#9810FA33] rounded-lg">
                                    <HistoryIcon color="#C27AFF" />
                                </span>
                                <Text tid="history" className="text-xl !text-[#F3E8FF]" />
                            </div>

                            {/* Hide hint on mobile root automatically */}
                            <Text
                                tid="history-page-hint"
                                className="!text-[#DAB2FF]"
                            />
                        </div>
                    )}
                </div>

                {/* DESKTOP NAV */}
                {!isMobile && (
                    <HistoryList activeLinkIndex={activeLinkIndex} />
                )}

                {/* MOBILE LIST */}
                {isMobile && isRootPage && (
                    <RHistoryList />
                )}

                {/* ACTIVE PAGE */}
                {(!isMobile || !isRootPage) && (
                    <CardLayout className="!bg-transparent !p-0 !border-none">

                        <div className="grid grid-cols-1 gap-5">
                            <Suspense fallback={<GlobalLoading />}>
                                {ActiveSubpage && <ActiveSubpage />}
                            </Suspense>
                        </div>
                    </CardLayout>
                )}
            </div>
        </MainLayout>
    )
}

export default TransactionsPage



const HistoryList = ({ activeLinkIndex }) => {
    return (
        <LayoutGroup id="history-nav">
            <div className="relative max-w-max flex gap-8 p-3 rounded-full bg-[#0F0524] overflow-x-auto scrollbar-hide">
                {SUB_NAVS.map((page, index) => {
                    const { Icon } = page;
                    const isActive = activeLinkIndex === index;

                    return (
                        <motion.div
                            key={index}
                            layout
                            className="relative rounded-full min-w-max"
                        >
                            {/* ACTIVE INDICATOR */}
                            {isActive && <HistoryTabIndicator />}

                            <Link
                                to={page.href}
                                className="relative z-10 hover:opacity-80 rounded-full"
                            >
                                <div className="flex gap-1 items-center justify-start p-1 px-2">
                                    <Icon
                                        size={16}
                                        color={isActive ? "#FFFFFF" : "#DAB2FF"}
                                    />
                                    <Text
                                        tid={page.title}
                                        className={`text-sm ${isActive ? "text-white" : "text-[#DAB2FF]"
                                            }`}
                                    />
                                </div>
                            </Link>
                        </motion.div>
                    );
                })}
            </div>
        </LayoutGroup>
    );
};

const RHistoryList = () => {
    const { main: { lang } } = useMainContext()
    return (
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
            {SUB_NAVS.map((page, index) => {
                const { Vector } = page
                return (
                    <Link
                        key={index}
                        to={page.href}
                        className='h-[152px] relative hover:opacity-80 overflow-hidden rounded-3xl'
                    >
                        <img src={require(`./assets/images/${page.image}-${lang}.png`)} className="z-[1] absolute left-0 top-0 w-full h-full" alt={page.image} />
                        <div className={`flex flex-col gap-1 items-start justify-start p-6 relative z-[2] w-2/3 `}>
                            <Text tid={page.title} className={`text-xl text-nowrap  `} />
                            <Text tid={page.desc} className={`whitespace-pre-wrap`} />
                        </div>
                    </Link>
                )
            })}
        </div>
    )
}


const HistoryTabIndicator = () => {
    return (
        <motion.div
            layoutId="history-indicator"
            className="history-indicator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
                type: "spring",
                stiffness: 520,
                damping: 35,
            }}
        />
    );
};
